import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useSelector } from "react-redux";
import { previewReviewerNotice, sendReviewerNotice } from "../../../../../api/reviewerNotice";
import { formatDate } from "../../../../../utils/getEntitiesUtils";

const Recipients = ({ label, emails }) => {
  if (!emails || emails.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mr: 0.5, minWidth: 40 }}>
        {label}
      </Typography>
      {emails.map((email) => (
        <Chip key={email} label={email} size="small" variant="outlined" />
      ))}
    </Box>
  );
};

/**
 * Vista previa y envío del aviso de revisor asignado para un grupo.
 *
 * Al abrirse pide al backend el mail ya rellenado (mismo código que el envío,
 * así lo que se ve es lo que sale) y lo muestra: destinatarios, asunto, cuerpo
 * y el link público al PDF. "Enviar" manda y avisa con el resultado; si el mail
 * no salió, lo dice y deja reintentar.
 */
const ReviewerNoticeDialog = ({ open, team, onClose, onSent, onEditTemplate }) => {
  const user = useSelector((state) => state.user);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !team) return;
    let cancelled = false;
    setPreview(null);
    setError(null);
    setResult(null);
    setLoading(true);
    previewReviewerNotice(user, team.id)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((e) => {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        setError(detail || "No se pudo armar la vista previa del aviso.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, team, user]);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const data = await sendReviewerNotice(user, team.id);
      setResult(data);
      if (data.sent) {
        onSent?.(team.id, data);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || "No se pudo enviar el aviso. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const alreadySent = preview?.reviewer_notified_at || result?.reviewer_notified_at;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Aviso de revisor asignado
        {team && (
          <Typography variant="subtitle1" color="text.secondary">
            Equipo {team.group_number}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {result?.sent && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {result.detail} Registrado el {formatDate(result.reviewer_notified_at)}.
          </Alert>
        )}
        {result && !result.sent && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {result.detail}
          </Alert>
        )}

        {preview && (
          <>
            {alreadySent && !result?.sent && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Este aviso ya se mandó el {formatDate(alreadySent)}. Podés volver a
                enviarlo: sale el mismo link y se actualiza la fecha.
              </Alert>
            )}

            <Recipients label="Para" emails={preview.to} />
            <Recipients label="CC" emails={preview.cc} />
            <Recipients label="CCO" emails={preview.bcc} />

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Asunto:</strong> {preview.subject}
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#fafafa" }}>
              <Typography
                variant="body2"
                component="div"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {preview.body_text}
              </Typography>
            </Paper>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Link público al PDF (cualquiera con el link puede descargarlo):
              </Typography>
              <Link href={preview.link_pdf} target="_blank" rel="noopener" variant="caption">
                abrir
              </Link>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button onClick={onEditTemplate} size="small">
          Editar plantilla
        </Button>
        <Box>
          <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>
            {result?.sent ? "Cerrar" : "Cancelar"}
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={!preview || sending || loading}
          >
            {result?.sent ? "Reenviar" : alreadySent ? "Reenviar" : "Enviar"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewerNoticeDialog;
